import {
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
  PLATFORM_ID,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { SkeletonModule } from 'primeng/skeleton';

import { PaymentService, ReceiptData } from '../../../services/payment';
import { ReceiptDownloader } from '../../../services/receipt-downloader';
import { ToastService } from '../../../services/toast';

/**
 * Presentational receipt — the dialog (success / list) and the /payments/success page share it.
 * Pass EITHER `[paymentId]` (it loads `rcData`) OR a preloaded `[data]` (no re-fetch, used by the
 * finder). Print / Download PDF are enabled only for SUCCESS payments (the backend 400s otherwise),
 * and both fetch bytes through HttpClient so the JWT is attached (never a raw href).
 */
@Component({
  selector: 'app-receipt-view',
  standalone: true,
  imports: [CommonModule, ButtonModule, TagModule, SkeletonModule],
  templateUrl: './receipt-view.html',
  styleUrls: ['./receipt-view.css'],
})
export class ReceiptView {
  private payment = inject(PaymentService);
  private downloader = inject(ReceiptDownloader);
  private toast = inject(ToastService);
  private platformId = inject(PLATFORM_ID);

  /** Provide one of these. `data` wins (no fetch); otherwise `paymentId` is loaded via rcData. */
  paymentId = input<number | null>(null);
  data = input<ReceiptData | null>(null);

  receipt = signal<ReceiptData | null>(null);
  loading = signal(false);
  notFound = signal(false);
  printing = signal(false);
  downloading = signal(false);
  /** Set by the logo <img>'s (error) handler — flips the header back to the issuer name. */
  logoFailed = signal(false);

  /** Dedupe loads so the effect doesn't re-fetch the same payment on unrelated re-runs. */
  private loadedKey = '';

  constructor() {
    effect(() => {
      const d = this.data();
      this.logoFailed.set(false); // new target -> give its logo a fresh chance
      if (d) {
        this.loadedKey = `data:${d.paymentId}`;
        this.receipt.set(d);
        this.notFound.set(false);
        return;
      }
      const id = this.paymentId();
      if (id && id > 0) this.load(id);
      // Target cleared — also reset the dedupe key + not-found flag, so returning to a
      // previously-loaded id on a reused instance re-fetches instead of showing a blank.
      else {
        this.loadedKey = '';
        this.notFound.set(false);
        this.receipt.set(null);
      }
    });
  }

  private load(id: number) {
    if (!isPlatformBrowser(this.platformId)) return; // SSR: defer to the post-hydration run
    if (this.loadedKey === `id:${id}`) return;
    this.loadedKey = `id:${id}`;
    this.loading.set(true);
    this.notFound.set(false);
    this.payment.rcData(id).subscribe({
      next: (res: any) => {
        this.loading.set(false);
        if (res?.success && res?.data) {
          this.receipt.set(res.data as ReceiptData);
        } else {
          this.receipt.set(null);
          this.notFound.set(true);
          this.toast.error(res?.message || 'Receipt not found.');
        }
      },
      error: (e) => {
        this.loading.set(false);
        this.receipt.set(null);
        this.notFound.set(true);
        this.toast.error(e?.error?.message || 'Could not load the receipt.');
      },
    });
  }

  // ===================== derived view state =====================
  readonly isSuccess = computed(
    () => (this.receipt()?.paymentStatus || '').toUpperCase() === 'SUCCESS',
  );

  /** Issuer name from the ReceiptSettings block; static fallback for older payloads. */
  readonly issuerName = computed(() => this.receipt()?.issuer?.name || 'Arogya Insurance');

  /**
   * UTC → IST display, matching the PDF (ReceiptHtmlBuilder converts +05:30 server-side).
   * The API's `…At` datetimes are UTC but serialize WITHOUT a timezone marker — `new Date()`
   * would read them as browser-local (showing the raw UTC clock labelled IST), so anchor
   * offset-less values to UTC explicitly before formatting in Asia/Kolkata.
   */
  istDate(s?: string | null, withTime = false): string {
    if (!s) return '—';
    const iso = /([zZ]|[+-]\d{2}:?\d{2})$/.test(s) ? s : s + 'Z';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    const opts: Intl.DateTimeFormatOptions = {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    };
    if (withTime) {
      opts.hour = 'numeric';
      opts.minute = '2-digit';
      opts.hour12 = true;
    }
    return new Intl.DateTimeFormat('en-IN', opts).format(d);
  }

  /** Currency symbol — ₹ for INR, otherwise the code as a prefix. */
  readonly sym = computed(() => {
    const c = (this.receipt()?.currency || 'INR').toUpperCase();
    return c === 'INR' ? '₹' : c + ' ';
  });

  /** One-line "address, city, state, pincode" with the blanks dropped. */
  readonly addressLine = computed(() => {
    const r = this.receipt();
    if (!r) return '';
    return [r.memberAddress, r.memberCity, r.memberState, r.memberPincode]
      .map((p) => (p ?? '').toString().trim())
      .filter(Boolean)
      .join(', ');
  });

  /**
   * Premium breakdown rows + total, built to MATCH the server receipt
   * (Utility/ReceiptHtmlBuilder) exactly so the on-screen view and the printed PDF agree:
   *   - "<policy> — Premium"  (basePremium, which ALREADY INCLUDES the doctor add-on)
   *   - when doctorConsultantAMT > 0, the premium line shows base − doctor and a separate
   *     "Doctor Consultant Charges" line is added (so the rows reconcile to the total)
   *   - "GST (pct%)" only when gstAmt > 0
   *   - muted "Sum Insured" only when sumInsured > 0
   * Total Paid = amount (the actual charged amount), matching the server's TotalPaid.
   */
  readonly view = computed(() => {
    const r = this.receipt();
    if (!r) return null;

    const items: { label: string; amount: number; muted: boolean }[] = [];
    const premiumLabel = r.masterPolicyName ? `${r.masterPolicyName} — Premium` : 'Insurance Premium';
    const baseFull = r.basePremium ?? r.amount ?? 0; // base falls back to the charged amount
    const doctor = r.doctorConsultantAMT ?? 0;

    if (doctor > 0) {
      items.push({ label: premiumLabel, amount: baseFull - doctor, muted: false });
      items.push({ label: 'Doctor Consultant Charges', amount: doctor, muted: false });
    } else {
      items.push({ label: premiumLabel, amount: baseFull, muted: false });
    }

    const gst = r.gstAmt ?? 0;
    if (gst > 0) {
      const label = r.gstPer != null && r.gstPer > 0 ? `GST (${r.gstPer}%)` : 'GST';
      items.push({ label, amount: gst, muted: false });
    }

    const si = r.sumInsured ?? 0;
    if (si > 0) items.push({ label: 'Sum Insured', amount: si, muted: true });

    return { items, total: r.amount ?? 0 };
  });

  statusSeverity(s?: string | null): 'success' | 'danger' | 'warn' | 'info' | 'secondary' {
    switch ((s || '').toUpperCase()) {
      case 'SUCCESS':
        return 'success';
      case 'FAILED':
      case 'CANCELLED':
        return 'danger';
      case 'REFUNDED':
        return 'info';
      case 'PENDING':
      case 'INITIATED':
        return 'warn';
      default:
        return 'secondary';
    }
  }

  // ===================== actions =====================
  async print() {
    const r = this.receipt();
    if (!r || this.printing()) return;
    this.printing.set(true);
    try {
      await this.downloader.printHtml(r.paymentId);
    } finally {
      this.printing.set(false);
    }
  }

  async download() {
    const r = this.receipt();
    if (!r || this.downloading()) return;
    this.downloading.set(true);
    try {
      await this.downloader.downloadPdf(r.paymentId, r.receiptNo);
    } finally {
      this.downloading.set(false);
    }
  }
}
